import React, { useMemo } from 'react';
import { useGlobalProject } from '@/contexts/GlobalProjectContext';
import { useProjectAnalytics } from '@/hooks/useProjectAnalytics';
import { EmptyState } from '@/components/analytics/EmptyState';
import LoadingState from '@/components/ui/LoadingState';
import { MilestoneTable } from '@/components/analytics/MilestoneTable';
import { AnalyticsStatusRow } from '@/components/analytics/AnalyticsStatusRow';
import { PageHeader } from '@/components/layout/PageHeader';
import { TableScaffold } from '@/components/ui/TableScaffold';

const MilestonesPage: React.FC = () => {
  const { selectedProject, selectedBaselineId } = useGlobalProject();
  const { loading, merged } = useProjectAnalytics();
  const errors = merged?.errors ?? [];

  const loaded = useMemo(() => ({
    schedule: !!merged,
    activities: !!(merged?.milestones?.length),
    risk: !!merged,
    baseline: !!selectedBaselineId,
  }), [merged, selectedBaselineId]);

  if (!selectedProject) {
    return (
      <div className="flex flex-col h-full">
        <EmptyState />
      </div>
    );
  }

  if (loading && !merged) {
    return (
      <div className="flex flex-col h-full">
        <LoadingState message="Loading milestones…" size="lg" fullScreen={false} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <PageHeader
        title="Milestone status"
        subtitle="Deliverables sorted by worst variance first."
        meta={<AnalyticsStatusRow loaded={loaded} errorCount={errors.length} errorMessages={errors} />}
      />
      {merged && (
        <TableScaffold toolbar={<span className="text-xs font-medium text-muted-foreground">Milestones at Risk</span>}>
          <MilestoneTable milestones={merged.milestones} loading={loading} />
        </TableScaffold>
      )}
      {!merged && !loading && (
        <p className="text-xs text-muted-foreground">No schedule data available. Select a project with activities.</p>
      )}
    </div>
  );
};

export default MilestonesPage;
